import type { AllergySeverity, AllergyMark } from '../api/types';
import { useI18n } from '../i18n/I18nProvider';
import { allergenIconUrl } from './allergenIcons';

export type AllergenIconSize = 'sm' | 'md' | 'lg';

/** Higher is more serious, so the badge on a photo picks the worst whatever order it is handed. */
const SEVERITY_RANK: Record<AllergySeverity, number> = { Mild: 0, Severe: 1, Anaphylactic: 2 };

/**
 * One allergen's picture on its plate (Q75), its ring telling how serious it is (Q76).
 *
 * The picture helps and the name decides: the name the nursery wrote, with the severity in words, is the element's
 * label and tooltip, so a screen reader and a long press both read the word a teacher needs. A severity is drawn in
 * shape as well as colour (thin ring, thick red ring, thick red ring and "!"), for whoever cannot tell the colours
 * apart.
 */
export function AllergenIcon({ icon, allergen, severity, size = 'md', label, className }: {
  icon: string | null | undefined;
  allergen: string;
  severity?: AllergySeverity | null;
  size?: AllergenIconSize;
  /** Words in place of this allergy's own, where the picture stands for several (the badge on a photo). */
  label?: string;
  className?: string;
}) {
  const { t } = useI18n();
  label ??= severity ? t('allergy.mark', { allergen, severity: t(`allergy.severity.${severity}`) }) : allergen;
  const modifiers = [`dg-allergen--${size}`, severity ? `dg-allergen--${severity.toLowerCase()}` : null, className];

  return (
    <span className={['dg-allergen', ...modifiers].filter(Boolean).join(' ')} role="img" aria-label={label} title={label}>
      <img src={allergenIconUrl(icon)} alt="" draggable={false} />
      {severity === 'Anaphylactic' && <span className="dg-allergen__alert" aria-hidden="true">!</span>}
    </span>
  );
}

/**
 * A child's allergies as one small circle on their photo: the most serious one's picture and ring, with every
 * allergy and its severity named in its label and tooltip. Nothing where the child has none.
 */
/** One formatter per locale, since a class list draws a badge for every child. */
const listFormats = new Map<string, Intl.ListFormat>();

function listFormat(locale: string): Intl.ListFormat {
  let format = listFormats.get(locale);
  if (!format) listFormats.set(locale, format = new Intl.ListFormat(locale, { type: 'conjunction' }));
  return format;
}

export function AllergyBadge({ allergies }: {
  allergies: readonly Pick<AllergyMark, 'allergen' | 'severity' | 'icon'>[];
}) {
  const { t, locale } = useI18n();
  if (allergies.length === 0) return null;

  const worst = allergies.reduce((a, b) => (SEVERITY_RANK[b.severity] > SEVERITY_RANK[a.severity] ? b : a));
  const each = allergies.map((a) => t('allergy.mark', { allergen: a.allergen, severity: t(`allergy.severity.${a.severity}`) }));
  const label = each.length === 1
    ? each[0]!
    : t('allergy.marks', { list: listFormat(locale).format(each) });

  return (
    <AllergenIcon
      icon={worst.icon} allergen={worst.allergen} severity={worst.severity} size="sm" label={label}
      className="dg-allergen--badge"
    />
  );
}
